import { motion } from "motion/react";
import {
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOf,
  oneOfType,
  shape,
  string,
} from "prop-types";
import React, { useId, useLayoutEffect, useMemo, useRef } from "react";

import styled from "styled-components/macro";

import { colors, colorsByFunction, marketingColors } from "UI/colors";

// Size tokens for the pill. `md` is the historical default; `sm` is the
// compact variant used by FnrAddressAutocomplete's Badge. Padding values
// are exported (SIZE_TOKENS) so consumers that need to size sibling slots
// against the OptionButton (e.g. Badge's CircleSlot) can read from the
// same source rather than mirroring the numbers locally.
export const SIZE_TOKENS = {
  md: {
    buttonPaddingX: 8,
    buttonPaddingY: 4,
    fontSize: 14,
    containerPadding: 2,
    containerGap: 4,
  },
  sm: {
    buttonPaddingX: 6,
    buttonPaddingY: 2,
    fontSize: 13,
    containerPadding: 1,
    containerGap: 3,
  },
};

const FnrOptionSwitch = ({
  options,
  selected,
  onSelect,
  className = "",
  displayElementLeft,
  displayElementRight,
  focusSize = "0px",
  activeColor = marketingColors.fnBlue,
  hoverColor = marketingColors.gray,
  fullWidth = false,
  noSpring = false,
  pillTransition,
  size = "md",
  gap,
  ...props
}) => {
  const sizeTokens = SIZE_TOKENS[size];
  // Caller can drive an animated collapse of the inter-button gap (e.g.
  // when one option's content slides to a zero-width "nothing" state).
  // Undefined → fall back to the size token's containerGap.
  const effectiveGap = gap === undefined ? sizeTokens.containerGap : gap;
  // motion's layoutId is global; suffix with useId so two switches on
  // the same page don't share a pill.
  const reactId = useId();
  const layoutId = `fnr-option-switch-pill-${reactId}`;

  // `selected === null` is an explicit "nothing selected" — no pill
  // renders and every option shows in the unselected state. `undefined`
  // keeps the legacy fallback (first option) so existing callers that
  // never pass `selected` still get a default selection.
  const selectedIndex = useMemo(() => {
    if (selected === null) return -1;
    if (selected !== undefined) {
      const index = options.findIndex((option) => option.id === selected);
      if (index >= 0) return index;
    }
    return 0;
  }, [selected, options]);

  const transition = useMemo(
    () =>
      pillTransition ||
      (noSpring
        ? { type: "tween", duration: 0.25, ease: "easeOut" }
        : { type: "spring", stiffness: 300, damping: 30 }),
    [pillTransition, noSpring]
  );

  // motion's transform: scale during the layoutId transit distorts
  // a CSS border-radius (a pill becomes an ellipse mid-flight) and
  // motion's own correction targets the rect at click time, which is
  // stale because the destination button is itself morphing. Fix:
  // observe the pill's style and rewrite border-radius from the live
  // scaleX so visual corners stay (half-height × half-height) every
  // frame. Source the radius from the pill's actual host button —
  // sibling buttons can have different heights (e.g. an icon makes
  // one taller than another) so a single sampled height would write
  // the wrong radius into the larger button's pill.
  const containerRef = useRef(null);
  const pillRef = useRef(null);
  useLayoutEffect(() => {
    const pill = pillRef.current;
    if (!pill) return undefined;
    const host = pill.parentElement;
    if (!host) return undefined;

    // Cache the last-written value so the MutationObserver we install
    // below — which fires on our own writes — bails out instead of
    // looping. Tracks the full string so radius changes (host resize)
    // also re-write, not just transform changes.
    let lastWritten = null;
    const compensate = () => {
      const r = host.offsetHeight / 2;
      if (!r) return;
      const scaleMatch = pill.style.transform?.match(/scale\(([\d.]+)/);
      const scaleX = scaleMatch ? parseFloat(scaleMatch[1]) || 1 : 1;
      const next = `${r / scaleX}px / ${r}px`;
      if (next === lastWritten) return;
      lastWritten = next;
      pill.style.borderRadius = next;
    };
    compensate();

    // Host height can change live — content morph between modes
    // (different icons / labels) resizes the OptionButton even when
    // the pill stays put. RO catches that.
    const ro = new ResizeObserver(compensate);
    ro.observe(host);
    // Transform scale changes mid-transit via motion's layoutId
    // animation — MutationObserver catches those style writes.
    const mo = new MutationObserver(compensate);
    mo.observe(pill, { attributes: true, attributeFilter: ["style"] });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [selectedIndex]);

  return (
    <ToggleContainer
      ref={containerRef}
      className={className}
      $fullWidth={fullWidth}
      $tokens={sizeTokens}
      $gap={effectiveGap}
      {...props}
    >
      {displayElementLeft ? displayElementLeft : null}

      {options.map((option, index) => {
        const isSelected = index === selectedIndex;
        // Per-option behavior. Defaults to "interactive" — a real
        // toggle button. "presentational" keeps the visual but drops
        // every interaction cue (no tab stop, no hover, no cursor, no
        // aria-pressed) — meant for status indicators that live in a
        // toggle slot but aren't a thing the user can pick. "hidden"
        // goes further: aria-hidden + no announcement, so the option
        // can occupy zero visible width without leaving a phantom
        // control behind for keyboard / screen-reader users.
        const behavior = option.behavior || "interactive";
        const interactive = behavior === "interactive";
        const hidden = behavior === "hidden";
        return (
          <OptionButton
            key={option.id}
            type="button"
            $isSelected={isSelected}
            $hoverColor={hoverColor}
            $tokens={sizeTokens}
            $interactive={interactive}
            $hidden={hidden}
            onClick={interactive ? () => onSelect?.(option) : undefined}
            aria-pressed={interactive ? isSelected : undefined}
            aria-label={hidden ? undefined : option.ariaLabel}
            aria-hidden={hidden || undefined}
            tabIndex={interactive ? 0 : -1}
            data-cy={option.dataCy}
          >
            {isSelected && (
              <HighlightPill
                ref={pillRef}
                layoutId={layoutId}
                // Without this, sibling morphs cause motion to restart
                // the layout animation each frame (~40 micro-tweens
                // per transit) and our configured transition is
                // effectively ignored.
                layoutDependency={selectedIndex}
                $activeColor={activeColor}
                initial={false}
                transition={transition}
              />
            )}
            <OptionContent>{option.value}</OptionContent>
          </OptionButton>
        );
      })}

      {displayElementRight ? displayElementRight : null}
    </ToggleContainer>
  );
};

export default FnrOptionSwitch;

FnrOptionSwitch.propTypes = {
  options: arrayOf(
    shape({
      id: oneOfType([number, string]).isRequired,
      value: node.isRequired,
      dataCy: string,
      ariaLabel: string,
      behavior: oneOf(["interactive", "presentational", "hidden"]),
    })
  ).isRequired,
  selected: oneOfType([number, string]),
  onSelect: func,
  className: string,
  displayElementLeft: node,
  displayElementRight: node,
  focusSize: string,
  activeColor: string,
  hoverColor: string,
  fullWidth: bool,
  noSpring: bool,
  pillTransition: object,
  size: oneOf(["md", "sm"]),
  gap: number,
};

const ToggleContainer = styled.div`
  position: relative;
  display: ${({ $fullWidth }) => ($fullWidth ? "flex" : "inline-flex")};
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  gap: ${({ $gap }) => $gap}px;
  transition: gap 0.3s ease-out;
  border: 1px solid ${colors.fnGray300};
  padding: ${({ $tokens }) => $tokens.containerPadding}px;
  border-radius: 9999px;
  background: white;
  box-sizing: border-box;

  ${({ $fullWidth }) =>
    $fullWidth &&
    `
    justify-content: space-between;

    & > button {
      flex: 1 1 0;
    }
  `}
`;

const OptionButton = styled.button`
  position: relative;
  background: none;
  border: none;
  /* A hidden option (zero-width content, e.g. the right slot in a pure-CTA
     mode) collapses its horizontal padding to 0 so it occupies no width —
     without this the empty button's own padding leaves phantom space on
     that side, making the pill asymmetric. The transition lets the padding
     morph back in (on the same 0.3s ease-out curve as the container gap and
     the FnrSlides content width) when a mode re-introduces a real right
     slot, so the space animates in/out instead of snapping. */
  padding: ${({ $tokens, $hidden }) =>
    `${$tokens.buttonPaddingY}px ${$hidden ? 0 : $tokens.buttonPaddingX}px`};
  border-radius: 9999px;
  cursor: ${({ $interactive }) => ($interactive ? "pointer" : "default")};
  font-size: ${({ $tokens }) => $tokens.fontSize}px;
  font-weight: 500;
  color: ${({ $isSelected }) =>
    $isSelected ? colorsByFunction.textLight : colorsByFunction.textSecondary};
  white-space: nowrap;
  transition: background-color 0.3s ease-in-out, color 0.3s,
    padding 0.3s ease-out;

  ${({ $interactive, $isSelected, $hoverColor }) =>
    $interactive
      ? `
    &:hover {
      color: ${$isSelected ? "#ffffff" : colorsByFunction.textSecondary};
      background-color: ${$isSelected ? "transparent" : $hoverColor};
    }

    &:focus-visible {
      background-color: ${$isSelected ? "transparent" : colors.fnGray100};
    }
  `
      : ""}
`;

// Lifts option content above the absolutely-positioned pill in the
// button's local stacking context.
const OptionContent = styled.span`
  position: relative;
  z-index: 1;
`;

const HighlightPill = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: ${({ $activeColor }) =>
    `linear-gradient(to bottom right, ${$activeColor}cc, ${$activeColor})`};
  pointer-events: none;
`;
